import type { FactBox } from "@/components/ui/box.schema";
import type { CourseContent, ContentMode } from "@/lib/api/courseContent";
import { ContentReadings } from "@/components/courses/content/CourseContent";
import { contentRead, useCourseContent } from "@/components/courses/content/use-course-content";

/** Explicit membership selects the topic; changing a session date never regroups it. */
export function topicForSession(content: CourseContent, sessionId: string) {
  const topicId = content.sessions.find(session => session.id === sessionId)?.topic_id;
  return content.topics.find(topic => topic.id === topicId);
}

/** Published topic preparation beside the exact session's existing plan owner. */
export function useSessionTopicBox(courseId: string, sessionId: string, teaching: boolean, enabled = true): FactBox<CourseContent> | null {
  const mode: ContentMode = teaching ? "published-preview" : "learner";
  const query = useCourseContent(courseId, mode, enabled && !!sessionId);
  if (!enabled || !sessionId) return null;
  const topic = query.data ? topicForSession(query.data, sessionId) : undefined;
  if (query.data && !query.isError && !topic) return null;
  return {
    id: "session-topic", kind: "fact", label: "Topic", job: "Read this session's published topic and reading references.",
    owner: { system: "imam" }, read: contentRead(query), fold: "never",
    prose: value => topicForSession(value, sessionId)?.title ?? "Topic",
    detail: () => "Published course content",
    body: value => {
      const selected = topicForSession(value, sessionId);
      if (!selected) return null;
      return <div className="grid gap-4">
        {selected.purpose ? <p className="whitespace-pre-wrap break-words">{selected.purpose}</p> : <p>No topic purpose is published.</p>}
        <ContentReadings readings={value.readings.filter(reading => reading.topic_id === selected.id)} timezone={value.timezone} />
        {value.course_status !== "published" && <p>Learners cannot read this course yet. Published content waits for course publication.</p>}
      </div>;
    },
    exits: topic ? [{ label: "Open topic", visit: `topic:${courseId}/${topic.id}` }] : undefined,
  };
}
